// lib/supabase/auth.ts
// Utility per autenticazione Supabase

import { getSupabaseBrowser } from './client';
import { createClient } from '@supabase/supabase-js';

// Client per componenti React (usa client esistente)
export function getSupabaseClient() {
  return getSupabaseBrowser();
}

// Client per server-side (API routes)
export function getSupabaseServerClient() {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
  const supabaseServiceKey = process.env.SUPABASE_SERVICE_ROLE_KEY!;
  
  return createClient(supabaseUrl, supabaseServiceKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  });
}

// Types per auth
export interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface SignInData {
  email: string;
  password: string;
}

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'free' | 'premium' | 'admin';
  ruolo: 'user' | 'admin';
  tenant_id: string | null;
  plan: string;
  plan_expires_at: string | null;
  onboarding_completed: boolean;
  preferred_disciplines: string[];
  study_goal_days: number | null;
  daily_study_minutes: number | null;
  avatar_url: string | null;
  created_at: string;
}

export interface UserLimits {
  role: string;
  plan: string;
  max_chat_queries_day: number;
  max_quiz_per_day: number | null;
  max_disciplines_active: number | null;
  max_simulations_per_week: number | null;
  history_days: number | null;
  max_pdf_exports_month: number | null;
  analytics_advanced: boolean;
  can_use_progressive_mode: boolean;
  can_use_expert_mode: boolean;
  can_unlock_all_modules: boolean;
}

// Funzioni auth client-side
export async function signUp(data: SignUpData) {
  const supabase = getSupabaseClient();
  
  const { data: authData, error } = await supabase.auth.signUp({
    email: data.email,
    password: data.password,
    options: {
      data: {
        first_name: data.firstName,
        last_name: data.lastName,
      },
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) throw error;
  return authData;
}

export async function signIn(data: SignInData) {
  const supabase = getSupabaseClient();
  
  const { data: authData, error } = await supabase.auth.signInWithPassword({
    email: data.email,
    password: data.password,
  });

  if (error) throw error;
  return authData;
}

export async function signOut() {
  const supabase = getSupabaseClient();
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getCurrentUser() {
  const supabase = getSupabaseClient();
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user ?? null;
}

export async function getUserProfile(): Promise<UserProfile | null> {
  const supabase = getSupabaseClient();
  const user = await getCurrentUser();
  
  if (!user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) throw error;
  
  return {
    ...data,
    email: user.email!,
  };
}

export async function getUserLimits(): Promise<UserLimits | null> {
  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase
    .rpc('get_user_limits')
    .single();

  if (error) throw error;
  return data as UserLimits | null;
}

export async function getQuizCountToday(): Promise<number> {
  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase.rpc('get_quiz_count_today');
  
  if (error) throw error;
  return data || 0;
}

export async function getSimulationsThisWeek(): Promise<number> {
  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase.rpc('get_simulations_this_week');
  
  if (error) throw error;
  return data || 0;
}

export async function canDoQuiz(): Promise<boolean> {
  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase.rpc('can_do_quiz');
  
  if (error) throw error;
  return data || false;
}

export async function canDoSimulation(): Promise<boolean> {
  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase.rpc('can_do_simulation');
  
  if (error) throw error;
  return data || false;
}

export async function incrementQuizCount(): Promise<void> {
  const supabase = getSupabaseClient();
  
  const { error } = await supabase.rpc('increment_quiz_count');
  
  if (error) throw error;
}

export async function incrementSimulationCount(): Promise<void> {
  const supabase = getSupabaseClient();
  
  const { error } = await supabase.rpc('increment_simulation_count');
  
  if (error) throw error;
}

export async function updateProfile(updates: Partial<UserProfile>): Promise<void> {
  const supabase = getSupabaseClient();
  const user = await getCurrentUser();
  
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', user.id);

  if (error) throw error;
}

export async function resetPassword(email: string): Promise<void> {
  const supabase = getSupabaseClient();
  
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/reset-password`,
  });

  if (error) throw error;
}

export async function getTenantConfig(): Promise<{
  brand: {
    nome_breve: string;
    colore_primario: string;
    colore_secondario: string;
    colore_testo: string;
    logo_url: string | null;
  } | null;
  toast: {
    testo: string;
    attivo: boolean;
    scade_il: string | null;
  } | null;
} | null> {
  const supabase = getSupabaseClient();
  const user = await getCurrentUser();
  console.log('[getTenantConfig] user:', user?.id ?? 'null');
  if (!user) return null;

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('tenant_id')
    .eq('id', user.id)
    .single();

  console.log('[getTenantConfig] profile:', profile, 'error:', profileError);
  if (!profile?.tenant_id) return null;

  const { data: tenant, error: tenantError } = await supabase
    .from('tenants')
    .select('config')
    .eq('id', profile.tenant_id)
    .single();

  console.log('[getTenantConfig] tenant:', tenant, 'error:', tenantError);
  return {
    brand: tenant?.config?.brand ?? null,
    toast: tenant?.config?.toast ?? null,
  };
}

export async function updatePassword(newPassword: string): Promise<void> {
  const supabase = getSupabaseClient();
  
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) throw error;
}